import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { IsolatedScan, IsolatedScanIMU, IsolatedScanMotion, IsolatedScanSystemData, IsolatedScanThermal, Loading, Motion, Scan, ScanMedia, ScanSection, SectionScan, Thermal, WorkflowAnalysis, WorkflowScans } from "../types/types"
import { getSectionScans } from "../service/sectionScanService"
import { getIsolatedScans } from "../service/isolatedScanService"
import { getScanMediaForSection } from "../service/scanMediaService"
import { getIsolatedScanImu } from "../service/isolatedScanImuService"
import { getWorkflowAnalysis } from "../service/workflowAnalysisService"
import { getIsolatedScanThermal } from "../service/isolatedScanThermalService"
import { getIsolatedScanMotion } from "../service/isolatedScanMotionService"
import { getIsolatedScanSystemData } from "../service/isolatedScanSystemData"

/**
 * UseAnalysis is responsible for managing all the state and logic for the Analysis page component.
 * 
 * @param {Function} updateToken callback function to clear token from session storage
 */
const useFetchAllWorkflowData = () => {

  const location = useLocation()
  const [workflowScans, setWorkflowScans] = useState<WorkflowScans | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<Loading>({ isLoading: false, text: "" })
  const [error, setError] = useState<Error | null>(null)

  const fetchWorkflowData = async () => {
    setLoadingText("Downloading workflow data.")

    const workflow = location.state
    if (workflow === undefined) {
      setError(new Error("Could not find workflow"))
      return
    }

    const sectionResponse = await getSectionScans(workflow.id)
    if (sectionResponse instanceof Error) { 
      setError(sectionResponse)
      return
      }
    const sections: SectionScan[] = sectionResponse

    const scanSections: ScanSection[] = []

    for (let i=0; i<sections.length; i++) {
      const sectionId = sections[i].id
      setLoadingText(`Downloading section ${i+1}.`)
      const scans = await getScansForSection(sectionId)
      if (isError(scans)) { return }

      setLoadingText(`Downloading section ${i+1} images.`)
      const media = await getScanMediaForSection(sectionId)
      if (isError(media)) { return }

      setLoadingText(`Downloading section ${i+1} imu.`)
      const imu = await getImu(sectionId)
      if (isError(imu)) { return }

      setLoadingText(`Downloading section ${i+1} IR data.`)
      const thermal = await getThermal(sectionId)
      if (isError(thermal)) { return }

      setLoadingText(`Downloading section ${i+1} optical.`)
      const motion = await getMotion(sectionId)
      if (isError(motion)) { return }

      setLoadingText(`Downloading section ${i+1} sys data.`)
      const systemData = await getSystemData(sectionId)        
      if (isError(systemData)) { return }
      setLoadingText(`Workflow downloaded.`)

      scanSections.push({
        sectionScan: sections[i],
        scans: scans as Scan[],
        imu: imu,
        motion: motion as Motion[],
        thermal: thermal as Thermal[],
        media: media as ScanMedia[],
        systemData: systemData as IsolatedScanSystemData[]
      })         
    }      

    const analysisResponse = await getWorkflowAnalysis(workflow.id)
    if (analysisResponse instanceof Error) {
      setError(analysisResponse)
    }

    const workflowScans: WorkflowScans = {
      workflow: workflow,
      analysis: analysisResponse as WorkflowAnalysis[],
      sections: scanSections
    }
    
    setWorkflowScans(workflowScans)    
    setLoadingFalse()
  }


  useEffect(() => {
    if (error !== null) {
      setLoadingFalse()      
    }
  }, [error])

  const getScansForSection = async (sectionId: number) => {
    const response = await getIsolatedScans(sectionId)

    if (response instanceof Error) {
      return response as Error
    }

    const scans: Scan[] = response.map((isolatedScan: IsolatedScan) => {
      return {
        id: isolatedScan.id,
        section_scan_id: isolatedScan.section_scan_id,
        scan_no: isolatedScan.scan_no,
        angle: isolatedScan.angle,
        date: isolatedScan.date,
        sensor_1: JSON.parse(isolatedScan.sensor_1),
        sensor_2: JSON.parse(isolatedScan.sensor_2),
        sensor_3: JSON.parse(isolatedScan.sensor_3),
        sensor_4: JSON.parse(isolatedScan.sensor_4),
        seq_nos: JSON.parse(isolatedScan.seq_nos),
        duration: parseInt(isolatedScan.duration),
        comment: isolatedScan.comment,
        calibration_start_p1: isolatedScan.calibration_start_p1,
        calibration_start_p2: isolatedScan.calibration_start_p2,
        calibration_end_p1: isolatedScan.calibration_end_p1,
        calibration_end_p2: isolatedScan.calibration_end_p2,
      }
    })
    return scans
  }

  const getImu = async (sectionId: number) => {
    const response = await getIsolatedScanImu(sectionId)

    if (response instanceof Error) {
      return response as Error
    }

    const imu = response.map((isolatedScanImu: IsolatedScanIMU) => {
      return {
        id: isolatedScanImu.id,
        section_scan_id: isolatedScanImu.section_scan_id,
        scan_no: isolatedScanImu.scan_no,
        seq_nos: JSON.parse(isolatedScanImu.seq_nos),
        metal: JSON.parse(isolatedScanImu.metal),
        roll_angles: JSON.parse(isolatedScanImu.roll_angles),
        pitch_angles: JSON.parse(isolatedScanImu.pitch_angles),
        inclination_angles: JSON.parse(isolatedScanImu.inclination_angles),
        accel_x: JSON.parse(isolatedScanImu.accel_x),
        accel_y: JSON.parse(isolatedScanImu.accel_y),
        accel_z: JSON.parse(isolatedScanImu.accel_z),
        gyro_x: JSON.parse(isolatedScanImu.gyro_x),
        gyro_y: JSON.parse(isolatedScanImu.gyro_y),
        gyro_z: JSON.parse(isolatedScanImu.gyro_z),
        date: isolatedScanImu.date,
      }
    })
    return imu
  }

  const getThermal = async (sectionId: number) => {
    const response = await getIsolatedScanThermal(sectionId)
    if (response instanceof Error) {
      return response
    }

    const thermal = response.map((isolatedScanThermal: IsolatedScanThermal) => {
      return {
        id: isolatedScanThermal.id,
        section_scan_id: isolatedScanThermal.section_scan_id,
        seq_nos: JSON.parse(isolatedScanThermal.seq_nos),
        sensor_1: JSON.parse(isolatedScanThermal.sensor_1),
        sensor_2: JSON.parse(isolatedScanThermal.sensor_2),
        scan_no: parseInt(isolatedScanThermal.scan_no),
        date: isolatedScanThermal.date
      }
    })
    return thermal
  }

  const getMotion = async (sectionScanId: number) => {
    const response = await getIsolatedScanMotion(sectionScanId)
    if (response instanceof Error) {
      return response
    }

    const motion = response.map((isolatedScanMotion: IsolatedScanMotion) => {
      return {
        id: isolatedScanMotion.id,
        section_scan_id: isolatedScanMotion.section_scan_id,
        seq_nos: JSON.parse(isolatedScanMotion.seq_nos),
        delta_x: JSON.parse(isolatedScanMotion.delta_x),
        delta_y: JSON.parse(isolatedScanMotion.delta_y),
        scan_no: parseInt(isolatedScanMotion.scan_no),
        date: isolatedScanMotion.date
      }
    }) 
    return motion
  }
  const isError = (response: any): boolean => {
    if (response instanceof Error) {
      setError(response)
      return true
    }
    return false
  }

  const getSystemData = async (sectionScanId: number) => {
    const response = await getIsolatedScanSystemData(sectionScanId)
    if (response instanceof Error) {
      return response
    }
    
    return response
  }

  const setLoadingText = (text: string) => {
    setIsLoading({ isLoading: true, text: text })
  }

  const setLoadingFalse = () => {
    setIsLoading((prev) => ({ ...prev, isLoading: false }))
  }

  return [{
    workflowScans,
    isLoading,
    error,
    fetchWorkflowData
  }]
}

export default useFetchAllWorkflowData